import { Pill, type Tone } from '@ps/ui-kit/components/pill';
import { money } from '@ps/ui-kit/lib/format';
import { Table } from '@ps/ui-kit/components/table';

// Every control of a gate evaluation: verdict, reason and evidence data (K-03 expanded row, K-06).

export const VERDICT_TONE: Record<string, Tone> = { PASS: 'ok', REFER: 'warn', BLOCK: 'bad' };

export function ControlsTable({ checks, label }: { checks: Array<{ control: string; verdict: string; reason: string; data?: unknown }>; label: string }) {
  return (
    <Table bare compact label={label}>
      <thead><tr><th>Control</th><th>Verdict</th><th>Reason</th><th>Data</th></tr></thead>
      <tbody>
        {checks.map((c, i) => (
          <tr key={`${c.control}-${i}`} className="align-top">
            <td className="font-mono font-semibold">{c.control}</td>
            <td><Pill tone={VERDICT_TONE[c.verdict]} upper>{c.verdict}</Pill></td>
            <td>{c.reason}</td>
            <td><ControlData data={c.data} /></td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Evidence in words, not raw JSON: screening hits as "listed name · score · list", other values as "label: value". */
function ControlData({ data }: { data: unknown }) {
  if (data === undefined || data === null) return <>—</>;
  if (typeof data !== 'object') return <>{String(data)}</>;
  const d = data as Record<string, unknown>;
  const hits = Array.isArray(d.hits) ? d.hits as Array<{ listed?: string; score?: number; types?: string[] }> : null;
  // Empty values (null, an empty list) say nothing to the reader, so they are left out.
  const rest = Object.entries(d).filter(([k, v]) => k !== 'hits' && v !== null && v !== undefined && !(Array.isArray(v) && !v.length));
  if (!hits?.length && !rest.length) return <>—</>;
  const label = (k: string) => k.replace(/Minor$/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase().replace(/^./, x => x.toUpperCase());
  const value = (k: string, v: unknown) => ((/Minor$/.test(k) || k === 'threshold') && typeof v === 'string' && /^\d+$/.test(v) ? money(v) : Array.isArray(v) ? v.join(', ') : typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v));
  return (
    <div className="space-y-0.5 text-ink-2">
      {hits && hits.map((h, i) => <p key={i}><span className="font-semibold text-ink">{h.listed ?? '—'}</span> · score {h.score ?? '—'}{h.types?.length ? ` · ${h.types.join(', ')}` : ''}</p>)}
      {rest.map(([k, v]) => <p key={k}><span className="text-ink-3">{label(k)}:</span> {value(k, v)}</p>)}
    </div>
  );
}
